import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';
/**
 * AI Assist API Client - Phase B2
 * Provides AI-powered assistance for Knowledge Pack forms
 */

/**
 * Get authorization header with JWT token
 */
const getAuthHeader = () => {
  // Try nexus_access_token first (newer auth), fall back to token (legacy)
  const token = secureStorage.getItem('nexus_access_token') || secureStorage.getItem('nexus_access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Suggest content for a specific field
 * 
 * @param {string} packType - Type of Knowledge Pack
 * @param {string} fieldName - Field name to generate content for
 * @param {object} context - Existing pack context
 * @returns {Promise<object>} - Suggestion result
 */
export const suggestContent = async (packType, fieldName, context = {}) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/v3/ai/suggest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        pack_type: packType,
        field_name: fieldName,
        context,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'AI suggestion failed');
    }

    return await response.json();
  } catch (error) {
    console.error('AI Suggest Error:', error);
    throw error;
  }
};

/**
 * Validate pack content for quality and compliance
 * 
 * @param {string} packType - Type of Knowledge Pack
 * @param {object} content - Pack content to validate
 * @returns {Promise<object>} - Validation result
 */
export const validateContent = async (packType, content) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/v3/ai/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        pack_type: packType,
        content,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'AI validation failed');
    }

    return await response.json();
  } catch (error) {
    console.error('AI Validate Error:', error);
    throw error;
  }
};

/**
 * Translate content between EN↔BM
 * 
 * @param {string} text - Text to translate
 * @param {string} sourceLang - Source language code (en or ms)
 * @param {string} targetLang - Target language code (en or ms)
 * @param {string} packType - Type of Knowledge Pack
 * @param {object} context - Additional context (brand terms, product names)
 * @returns {Promise<object>} - Translation result
 */
export const translateContent = async (text, sourceLang, targetLang, packType, context = {}) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/v3/ai/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        text,
        source_lang: sourceLang,
        target_lang: targetLang,
        pack_type: packType,
        context,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'AI translation failed');
    }

    return await response.json();
  } catch (error) {
    console.error('AI Translate Error:', error);
    throw error;
  }
};

/**
 * Rewrite content to improve tone, clarity, compliance, etc.
 * 
 * @param {string} text - Text to rewrite
 * @param {string} intent - What to improve (tone, clarity, compliance, engagement, conciseness)
 * @param {string} packType - Type of Knowledge Pack
 * @param {object} context - Additional context
 * @returns {Promise<object>} - Rewrite result
 */
export const rewriteContent = async (text, intent, packType, context = {}) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/v3/ai/rewrite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        text,
        intent,
        pack_type: packType,
        context,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'AI rewrite failed');
    }

    return await response.json();
  } catch (error) {
    console.error('AI Rewrite Error:', error);
    throw error;
  }
};

export default {
  suggestContent,
  validateContent,
  translateContent,
  rewriteContent,
};
